import React from 'react';

import {Box, Button, ButtonBase, Menu, MenuItem, Stack, TextField, ThinkingDots, Typography} from 'views/components';
import {AddOutlined, RefreshOutlined, SendOutlined} from 'views/icons';
import MarkdownViewer from 'views/module/markdown/MarkdownViewer';

import backend from 'utils/backend';
import moment from 'moment';
import {useSearchParams} from 'react-router-dom';

const AgentChatScene = () => {
  const USAGE_KEYS = ['inputTokens', 'outputTokens', 'totalTokens'];

  const addUsage = (current, incoming) => {
    if (!current && !incoming) return null;
    const result = USAGE_KEYS.reduce((acc, key) => {
      acc[key] = (current?.[key] || 0) + (incoming?.[key] || 0);
      return acc;
    }, {});
    if (current?.costUsd !== undefined || incoming?.costUsd !== undefined) {
      result.costUsd = (current?.costUsd || 0) + (incoming?.costUsd || 0);
    }
    return result;
  };

  const compactTokens = new Intl.NumberFormat('en', {notation: 'compact'});
  // Bedrock bills in USD while the rest of the admin renders CAD, so show the code
  // rather than a bare '$'. Rounding to the cent is display-only — the stored value
  // keeps sub-cent precision so per-turn costs still add up correctly on the session.
  const formatCost = new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'code',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const formatTokens = (usage) => {
    if (!usage) return null;
    const tokens = `${compactTokens.format(usage.totalTokens || 0)} tokens`;
    return usage.costUsd ? `${tokens} · ${formatCost.format(usage.costUsd)}` : tokens;
  };

  const getMessageText = (message) => message.content || message.text || '';
  const isAgentSender = (senderRn) => typeof senderRn === 'string' && senderRn.startsWith('RN:AGENT');

  const mapSessionMessages = (messages) =>
    messages.map((m) => ({
      role: isAgentSender(m.senderRn) ? 'assistant' : 'user',
      text: getMessageText(m),
      createdAt: m.createdAt || null,
      usage: m.usage || null,
    }));

  const [searchParams, setSearchParams] = useSearchParams();

  const [sessions, setSessions] = React.useState([]);
  const [selectedSessionRn, setSelectedSessionRn] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [streamingText, setStreamingText] = React.useState('');
  const [liveUsage, setLiveUsage] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [newChatMenuAnchor, setNewChatMenuAnchor] = React.useState(null);
  const bottomRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const initialLoadDone = React.useRef(false);
  const initialMessageSent = React.useRef(false);
  const streamingTextRef = React.useRef('');
  const liveUsageRef = React.useRef(null);

  const isEventRouterDraft = !selectedSessionRn && searchParams.get('target') === 'eventRouterDiscipline';
  const draftCaseRn = searchParams.get('caseRn');
  const initialDraftMessage = searchParams.get('message');

  const loadSessions = React.useCallback(async () => {
    try {
      const result = await backend.agentChat.listSessions();
      setSessions(result.sessions || []);
      return result.sessions || [];
    } catch {
      return [];
    }
  }, []);

  React.useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;

    const paramSessionRn = searchParams.get('sessionRn');

    loadSessions().then((loaded) => {
      if (paramSessionRn) {
        handleSelectSession(paramSessionRn);
      } else if (loaded.length > 0) {
        // don't auto-select — let user pick
      }
    });
  }, []);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({behavior: 'smooth'});
  }, [messages, loading, streamingText]);

  const handleSelectSession = React.useCallback(async (sessionRn) => {
    try {
      const result = await backend.agentChat.getSession(sessionRn);
      setSelectedSessionRn(sessionRn);
      setMessages(mapSessionMessages(result.messages || []));
      setError(null);
      setSearchParams({sessionRn}, {replace: true});
    } catch {
      setError('Failed to load session.');
    }
  }, [setSearchParams]);

  const handleNewChat = React.useCallback((target) => {
    setNewChatMenuAnchor(null);
    setSelectedSessionRn(null);
    setMessages([]);
    setError(null);
    setInput('');
    setSearchParams(target ? {target} : {}, {replace: true});
    inputRef.current?.focus();
  }, [setSearchParams]);

  const sendMessage = React.useCallback(async (messageOverride) => {
    const messageSource = typeof messageOverride === 'string' ? messageOverride : input;
    const message = messageSource.trim();
    if (!message || loading) return;

    setInput('');
    setError(null);
    setMessages((prev) => [...prev, {role: 'user', text: message, createdAt: moment().toISOString()}]);
    setLoading(true);
    setStreamingText('');
    setLiveUsage(null);
    streamingTextRef.current = '';
    liveUsageRef.current = null;

    const ws = new backend.agentChat.AgentChatWebsocketManager({
      onInit: (data) => {
        if (!selectedSessionRn) {
          setSelectedSessionRn(data.session.sessionRn);
          setSessions((prev) => [data.session, ...prev]);
          setSearchParams({sessionRn: data.session.sessionRn}, {replace: true});
        }
      },
      onChunk: (data) => {
        streamingTextRef.current += data.chunk;
        setStreamingText(streamingTextRef.current);
      },
      onUsage: (data) => {
        if (!data.usage) return;
        setLiveUsage((current) => {
          let next = current;
          if (data.scope === 'TURN') {
            next = data.usage;
          } else if (data.scope === 'MODEL_CALL') {
            next = addUsage(current, data.usage);
          }
          liveUsageRef.current = next;
          return next;
        });
      },
      onDone: (data) => {
        const assistantMessage = data.assistantMessage || {};
        const usage = assistantMessage.usage || liveUsageRef.current;

        setMessages((prev) => [...prev, {
          role: 'assistant',
          text: streamingTextRef.current || getMessageText(assistantMessage),
          createdAt: assistantMessage.createdAt || moment().toISOString(),
          usage: usage || null,
        }]);
        setStreamingText('');
        setLiveUsage(null);
        streamingTextRef.current = '';
        liveUsageRef.current = null;
        setLoading(false);
        loadSessions();
        ws.close();
      },
      onError: (data) => {
        setError(data.message || 'Failed to get a response. Please try again.');
        setStreamingText('');
        setLiveUsage(null);
        streamingTextRef.current = '';
        liveUsageRef.current = null;
        setLoading(false);
        ws.close();
      },
      onClose: () => {
        inputRef.current?.focus();
      },
    });

    try {
      await ws.connect(message, selectedSessionRn, isEventRouterDraft ? {
        agentTarget: 'EVENT_ROUTER_DISCIPLINE',
        caseRn: draftCaseRn || undefined,
      } : {});
    } catch {
      setError('Authentication error. Please refresh and try again.');
      setLiveUsage(null);
      liveUsageRef.current = null;
      setLoading(false);
    }
  }, [draftCaseRn, input, isEventRouterDraft, loadSessions, loading, selectedSessionRn, setSearchParams]);

  React.useEffect(() => {
    if (initialMessageSent.current || !isEventRouterDraft || !initialDraftMessage?.trim()) {
      return;
    }

    initialMessageSent.current = true;
    sendMessage(initialDraftMessage);
  }, [initialDraftMessage, isEventRouterDraft, sendMessage]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Stack
      direction={{xs: 'column', md: 'row'}}
      alignItems='stretch'
      sx={{width: '100%', height: {md: 'calc(99vh - 32px)'}}}
    >
      {/* Sessions list */}
      <Box sx={{flexShrink: 0, alignSelf: 'stretch', display: 'flex'}}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            width: '280px',
            border: '1px solid',
            borderColor: '#E8E4DE',
            overflow: 'hidden',
          }}
        >
          <Stack
            direction='row'
            alignItems='flex-start'
            justifyContent='space-between'
            sx={{px: '32px', paddingTop: '24px', paddingBottom: '12px', gap: '8px'}}
          >
            <Stack sx={{gap: '8px'}}>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '17px',
                  color: '#1A1916',
                  lineHeight: '100%',
                  textDecoration: 'underline',
                  textDecorationColor: '#DA7955',
                  textUnderlineOffset: '5px',
                }}
              >
                Chat
              </Typography>
              <Typography sx={{fontWeight: 400, fontSize: '12px', color: '#A8A49E', lineHeight: '100%', whiteSpace: 'nowrap'}}>
                Conversation history
              </Typography>
            </Stack>
            <Stack direction='row' sx={{gap: '4px'}}>
              <Button
                onClick={(e) => setNewChatMenuAnchor(e.currentTarget)}
                disableElevation
                title='New conversation'
                sx={{
                  'minWidth': '0',
                  'borderRadius': '7px',
                  'p': '4px',
                  'color': '#A8A49E',
                  'backgroundColor': 'transparent',
                  '&:hover': {backgroundColor: '#F3F1EE', boxShadow: 'none'},
                }}
              >
                <AddOutlined sx={{fontSize: 16}} />
              </Button>
              <Menu
                anchorEl={newChatMenuAnchor}
                open={Boolean(newChatMenuAnchor)}
                onClose={() => setNewChatMenuAnchor(null)}
                disableRestoreFocus
              >
                <MenuItem onClick={() => handleNewChat()} sx={{fontSize: '13px', color: '#1A1916'}}>
                  Chanel
                </MenuItem>
                <MenuItem onClick={() => handleNewChat('eventRouterDiscipline')} sx={{fontSize: '13px', color: '#1A1916'}}>
                  Hudson
                </MenuItem>
              </Menu>
              <Button
                onClick={loadSessions}
                disableElevation
                title='Refresh'
                sx={{
                  'minWidth': '0',
                  'borderRadius': '7px',
                  'p': '4px',
                  'color': '#A8A49E',
                  'backgroundColor': 'transparent',
                  '&:hover': {backgroundColor: '#F3F1EE', boxShadow: 'none'},
                }}
              >
                <RefreshOutlined sx={{fontSize: 16}} />
              </Button>
            </Stack>
          </Stack>

          <Box sx={{borderBottom: '1px solid #E8E4DE'}} />

          <Stack sx={{flex: 1, px: '8px', py: '8px', gap: '2px', overflowY: 'auto'}}>
            {sessions.length === 0 && (
              <Typography sx={{px: '8px', py: '8px', fontSize: '12px', color: '#A8A49E', lineHeight: '150%'}}>
                No conversations yet
              </Typography>
            )}
            {sessions.map((session) => {
              const isSelected = session.sessionRn === selectedSessionRn;
              return (
                <ButtonBase
                  key={session.sessionRn}
                  onClick={() => handleSelectSession(session.sessionRn)}
                  sx={{
                    'position': 'relative',
                    'display': 'flex',
                    'alignItems': 'flex-start',
                    'flexDirection': 'column',
                    'width': '100%',
                    'px': '12px',
                    'py': '10px',
                    'borderRadius': '8px',
                    'textAlign': 'left',
                    'backgroundColor': isSelected ? '#F3F1EE' : 'transparent',
                    '&:hover': {backgroundColor: isSelected ? '#F3F1EE' : '#F8F6F3'},
                    '&::before': isSelected ? {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '20%',
                      bottom: '20%',
                      width: '4px',
                      borderRadius: '0 2px 2px 0',
                      backgroundColor: '#DA7955',
                    } : {},
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: isSelected ? 500 : 400,
                      color: '#1A1916',
                      lineHeight: '130%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%',
                    }}
                  >
                    {session.title || 'Untitled'}
                  </Typography>
                  <Stack direction='row' alignItems='center' sx={{gap: '6px', mt: '3px'}}>
                    <Typography sx={{fontSize: '11px', color: '#A8A49E', lineHeight: '100%'}}>
                      {moment(session.updatedAt).format('MMM D, YYYY')}
                    </Typography>
                    {session.agentTarget === 'EVENT_ROUTER_DISCIPLINE' && (
                      <Typography
                        sx={{
                          fontSize: '9px',
                          fontWeight: 500,
                          color: '#DA7955',
                          lineHeight: '100%',
                          letterSpacing: '0.4px',
                          textTransform: 'uppercase',
                          px: '5px',
                          py: '2px',
                          borderRadius: '999px',
                          border: '1px solid #F0D5C9',
                          backgroundColor: '#FBF3EF',
                        }}
                      >
                        Hudson
                      </Typography>
                    )}
                  </Stack>
                  {session.usage && (
                    <Typography sx={{fontSize: '10px', color: '#A8A49E', lineHeight: '100%', mt: '4px'}}>
                      {formatTokens(session.usage)}
                    </Typography>
                  )}
                </ButtonBase>
              );
            })}
          </Stack>
        </Box>
      </Box>

      {/* Chat panel */}
      <Box sx={{flex: 1, display: 'flex', backgroundColor: '#FFFFFF'}}>
        <Box sx={{width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
          {/* Messages */}
          <Box sx={{flex: 1, overflowY: 'auto', px: '40px', py: '32px'}}>
            {messages.length === 0 && !loading && (
              <Box sx={{display: 'flex', justifyContent: 'center', pt: '60px'}}>
                <Typography sx={{fontSize: '14px', color: '#A8A49E'}}>
                  {selectedSessionRn ? 'No messages in this session.' : `Start a conversation with ${isEventRouterDraft ? 'Hudson' : 'Chanel'}`}
                </Typography>
              </Box>
            )}
            <Stack sx={{gap: '16px', maxWidth: '720px', mx: 'auto'}}>
              {messages.map((msg, i) => (
                <Box key={i} sx={{display: 'flex', flexDirection: 'column',
                  alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start'}}>
                  <Box
                    sx={{
                      maxWidth: '78%',
                      px: '14px',
                      py: '10px',
                      borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                      backgroundColor: msg.role === 'user' ? '#DA7955' : '#F8F6F3',
                      border: msg.role === 'assistant' ? '1px solid #E8E4DE' : 'none',
                    }}
                  >
                    {msg.role === 'assistant' ? (
                      <MarkdownViewer value={msg.text} />
                    ) : (
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 400,
                          color: '#FFFFFF',
                          lineHeight: '160%',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {msg.text}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction='row' sx={{gap: '6px', mt: '4px', px: '4px'}}>
                    {msg.createdAt && moment(msg.createdAt).isValid() && (
                      <Typography sx={{fontSize: '10px', color: '#A8A49E', lineHeight: '100%'}}>
                        {moment(msg.createdAt).format('MMM. D, YYYY HH:mm')}
                      </Typography>
                    )}
                    {msg.role === 'assistant' && msg.usage && (
                      <Typography sx={{fontSize: '10px', color: '#A8A49E', lineHeight: '100%'}}>
                        {formatTokens(msg.usage)}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              ))}

              {loading && !streamingText && (
                <Box sx={{display: 'flex', justifyContent: 'flex-start'}}>
                  <Box sx={{px: '14px', py: '12px', borderRadius: '14px 14px 14px 4px', backgroundColor: '#F8F6F3', border: '1px solid #E8E4DE'}}>
                    <ThinkingDots />
                  </Box>
                </Box>
              )}
              {streamingText && (
                <Box sx={{display: 'flex', justifyContent: 'flex-start'}}>
                  <Box sx={{maxWidth: '78%', px: '14px', py: '10px', borderRadius: '14px 14px 14px 4px',
                    backgroundColor: '#F8F6F3', border: '1px solid #E8E4DE'}}>
                    <MarkdownViewer value={streamingText} />
                  </Box>
                </Box>
              )}
              {loading && liveUsage && (
                <Stack direction='row' alignItems='center' sx={{gap: '6px', px: '4px', mt: '-10px'}}>
                  <Typography sx={{fontSize: '10px', color: '#A8A49E', lineHeight: '100%'}}>
                    {formatTokens(liveUsage)}
                  </Typography>
                </Stack>
              )}

              <div ref={bottomRef} />
            </Stack>
          </Box>

          {/* Error */}
          {error && (
            <Box sx={{px: '40px', py: '8px', backgroundColor: '#FEF2F2', borderTop: '1px solid #FECACA'}}>
              <Typography sx={{fontSize: '12px', color: '#EF4444', maxWidth: '720px', mx: 'auto'}}>
                {error}
              </Typography>
            </Box>
          )}

          {/* Input */}
          <Box sx={{px: '40px', py: '20px', borderTop: '1px solid #E8E4DE', flexShrink: 0}}>
            <Stack direction='row' alignItems='flex-end' sx={{gap: '10px', maxWidth: '720px', mx: 'auto'}}>
              <Box
                sx={{
                  'flex': 1,
                  'borderRadius': '10px',
                  'border': '1px solid #E8E4DE',
                  'backgroundColor': '#FBFBFB',
                  'overflow': 'hidden',
                  '&:focus-within': {borderColor: '#DA7955'},
                }}
              >
                <TextField
                  inputRef={inputRef}
                  value={input}
                  onChange={setInput}
                  onKeyDown={handleKeyDown}
                  multiline
                  maxRows={6}
                  placeholder='Message the agent...'
                  fullWidth
                  variant='standard'
                  disabled={loading}
                  sx={{
                    '& .MuiInputBase-root': {backgroundColor: 'transparent', px: '14px', py: '12px'},
                    '& .MuiInputBase-input': {fontSize: '14px', color: '#1A1916', lineHeight: '160%'},
                    '& .MuiInputBase-input::placeholder': {color: '#A8A49E', opacity: 1},
                    '& .MuiInput-underline:before': {borderBottom: '0'},
                    '& .MuiInput-underline:after': {borderBottom: '0'},
                    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {borderBottom: '0'},
                  }}
                />
              </Box>
              <Button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                disableElevation
                sx={{
                  'minWidth': '0',
                  'width': '40px',
                  'height': '40px',
                  'borderRadius': '10px',
                  'p': 0,
                  'backgroundColor': '#DA7955',
                  'color': '#FFFFFF',
                  '&:hover': {backgroundColor: '#DA7955', boxShadow: 'none'},
                  '&.Mui-disabled': {backgroundColor: '#E8E4DE', color: '#A8A49E'},
                }}
              >
                <SendOutlined sx={{fontSize: 17}} />
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
};

export default AgentChatScene;
